import { PropsWithChildren, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import moment from "moment";
import toast from "react-hot-toast";
import { Logger } from "../../utils/logger";

interface PropsType {
  formId?: string;
  open?: boolean;
  handleClose?: React.Dispatch<React.SetStateAction<boolean>>;
  formDetails?: any;
  formFields?: any;
}

const FormResponse: React.FC<PropsWithChildren<PropsType>> = ({
  formId,
  open = true,
  handleClose,
  formDetails,
  formFields,
}) => {
  const [formData, setFormData] = useState<any>(null);
  const [createdAt, setCreatedAt] = useState<string>("");
  const [fileFields, setFileFields] = useState<string[]>([]);
  const [fieldDisplayNames, setFieldDisplayNames] = useState<any>({});
  const StoreData = useSelector((state: any) => {
    return state?.rootReducers;
  });

  // Comprehensive list of system fields to exclude
  const SYSTEM_FIELDS = [
    "_id",
    "createdBy",
    "organization",
    "list",
    "integration",
    "label",
    "status",
    "customForms",
    "integrationName",
    "createdByName",
    "duplicateLeads",
    "activities",
    "lastActivityAt",
    "lastActivityPerformedAt",
    "lastActivityType",
    "sequenceId",
    "createdAt",
    "updatedAt",
    "__v",
  ];

  useEffect(() => {
    if (formId && !formDetails) {
      setFormData({
        message: `Viewing responses for form: ${formId}`,
        placeholder: "Form responses will be displayed here",
      });
      setCreatedAt(moment().format("DD MMM YYYY [at] hh:mm A"));
      return;
    }

    if (formDetails && formDetails?._id) {
      // Backend sends data already grouped: { question_1: { GroupName: { field: value } }, regularField: value }
      // Just render it directly!

      const organizedData: any = {};

      Object.entries(formDetails.formData).forEach(([key, value]) => {
        // Skip system fields
        if (SYSTEM_FIELDS.includes(key)) return;

        // Check if this is a question_N key with nested group data
        if (key.startsWith('question_') && typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // This is grouped data: { GroupName: { field: value } }
          Object.entries(value).forEach(([groupName, groupData]) => {
            if (typeof groupData === 'object' && groupData !== null && !Array.isArray(groupData)) {
              // Store as group with _isGroup flag
              organizedData[groupName] = {
                _isGroup: true,
                _groupTitle: groupName.replace(/_/g, ' '), // Convert underscores to spaces for display
                _groupFields: groupData
              };
            }
          });
        } else {
          // Regular field at root level
          organizedData[key] = value;
        }
      });

      setFormData(organizedData);

      const files: string[] = [];
      Object.entries(organizedData).forEach(([key, value]) => {
        if (value && typeof value === 'object' && (value as any)._isGroup) {
          // Check group fields for files
          Object.entries((value as any)._groupFields).forEach(([fieldKey, fieldValue]) => {
            if (isFileField(fieldKey, fieldValue)) {
              files.push(fieldKey);
            }
          });
        } else if (isFileField(key, value)) {
          files.push(key);
        }
      });
      setFileFields(files);

      setCreatedAt(moment(formDetails?.createdAt).format("DD MMM YYYY [at] hh:mm A"));
    }
  }, [formId, formDetails, formFields]);

  const isFileField = (key: string, value: any): boolean => {
    const lowerKey = key.toLowerCase();
    return (
      (lowerKey.includes("file") || lowerKey.includes("upload")) &&
      Array.isArray(value)
    );
  };

  const downloadResponse = () => {
    if (!formData) {
      toast.error("No response data to download");
      return;
    }

    try {
      // Build CSV
      const headers = ["Field", "Value"];
      const rows: string[] = [headers.join(",")];

      // Add metadata
      rows.push(`"Submitted On","${createdAt}"`);
      rows.push(`"Form Name","${formDetails?.customForm || "Form Response"}"`);
      rows.push(`"Lead Name","${formDetails?.leadName || "Unknown"}"`);
      rows.push(""); // Empty line

      // Add field data
      Object.keys(formData).forEach((key) => {
        const fieldValue = formData[key];

        // Check if this is a group field
        if (fieldValue && typeof fieldValue === 'object' && fieldValue._isGroup) {
          // Add group header
          rows.push(`"--- ${fieldValue._groupTitle} ---",""`);

          // Add nested fields
          Object.keys(fieldValue._groupFields).forEach((nestedKey) => {
            // Use displayName from metadata, fallback to formatted fieldName
            const nestedFieldName = fieldValue._fieldMetadata?.[nestedKey] || nestedKey.split("_").join(" ");
            let nestedValue = fieldValue._groupFields[nestedKey];

            // Handle arrays
            if (Array.isArray(nestedValue)) {
              nestedValue = nestedValue.join("; ");
            }

            // Escape and quote
            nestedValue = String(nestedValue).replace(/"/g, '""');
            if (nestedValue.includes(',') || nestedValue.includes('"') || nestedValue.includes('\n')) {
              nestedValue = `"${nestedValue}"`;
            }

            rows.push(`"  ${nestedFieldName}",${nestedValue}`);
          });

          rows.push(""); // Empty line after group
        } else {
          // Regular field - use displayName from metadata map
          const fieldName = fieldDisplayNames[key] || key.split("_").join(" ");
          let value = fieldValue;

          // Handle arrays
          if (Array.isArray(value)) {
            value = value.join("; ");
          }

          // Escape and quote
          value = String(value).replace(/"/g, '""');
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            value = `"${value}"`;
          }

          rows.push(`"${fieldName}",${value}`);
        }
      });

      const csvContent = rows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `response-${formDetails?.leadName || "data"}-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Response downloaded as CSV");
    } catch (error) {
      Logger.log("Download error:", error);
      toast.error("Failed to download");
    }
  };

  const downloadAllFiles = async () => {
    if (fileFields.length === 0) {
      toast.error("No files to download");
      return;
    }

    try {
      let downloadCount = 0;
      const bucketUrl = StoreData?.userData?.userDetails?.bucketUrl || "";

      fileFields.forEach((fieldKey) => {
        const files = formData[fieldKey];
        if (Array.isArray(files)) {
          files.forEach((fileUrl: string) => {
            const fileName = fileUrl.split("/")[fileUrl.split("/").length - 1];
            const fullUrl = `${bucketUrl}${fileUrl}`;

            const link = document.createElement("a");
            link.href = fullUrl;
            link.download = fileName;
            link.target = "_blank";
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            downloadCount++;
          });
        }
      });

      if (downloadCount > 0) {
        toast.success(`Downloading ${downloadCount} file${downloadCount > 1 ? "s" : ""}`);
      }
    } catch (error) {
      Logger.log("Download all files error:", error);
      toast.error("Failed to download files");
    }
  };

  const totalFiles = fileFields.reduce((count, fieldKey) => {
    const files = formData?.[fieldKey];
    return count + (Array.isArray(files) ? files.length : 0);
  }, 0);

  return (
    <Root>
      <div className="drawer-header">
        <h3>Response Details</h3>
        <div className="action-btns">
          {totalFiles > 0 && (
            <button className="btn btn-files" onClick={downloadAllFiles}>
              Download Files ({totalFiles})
            </button>
          )}
          <button className="btn btn-download" onClick={downloadResponse}>
            Download Response
          </button>
        </div>
      </div>

      {createdAt && (
        <div className="submission-info">
          Submitted on {createdAt}
        </div>
      )}

      <div className="drawer-content">
        {!formData ? (
          <div className="empty-state">
            <p>No Record Found</p>
          </div>
        ) : Object.keys(formData).length === 0 ? (
          <div className="empty-state">
            <p>No Fields Submitted</p>
          </div>
        ) : (
          <div className="fields-list">
            {Object.keys(formData).map((key: any, index: number) => {
              const fieldValue = formData[key];

              // Check if this is a group field
              if (fieldValue && typeof fieldValue === 'object' && fieldValue._isGroup) {
                return (
                  <div className="group-field-card" key={index}>
                    <div className="group-header">
                      <span className="group-icon">📋</span>
                      <h3 className="group-title">{fieldValue._groupTitle}</h3>
                    </div>
                    <div className="group-content">
                      {Object.keys(fieldValue._groupFields).map((nestedKey: string, nestedIdx: number) => {
                        const nestedValue = fieldValue._groupFields[nestedKey];
                        // Use displayName from metadata, fallback to formatted fieldName
                        const nestedLabel = fieldValue._fieldMetadata?.[nestedKey] || nestedKey.split("_").join(" ");

                        return (
                          <div className="nested-field" key={nestedIdx}>
                            <label className="nested-label">{nestedLabel}</label>
                            <div className="nested-value">
                              {Array.isArray(nestedValue) ? (
                                <div className="array-list">
                                  {nestedValue.map((item: any, idx: number) => (
                                    <span key={idx} className="array-tag">
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span>{nestedValue || <span className="no-value">No value</span>}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // Regular field rendering
              // Use displayName from the metadata map, fallback to formatted key
              const fieldLabel = fieldDisplayNames[key] || key.split("_").join(" ");

              return (
                <div className="field-card" key={index}>
                  <div className="field-header">
                    <span className="field-num">{index + 1}</span>
                    <h4 className="field-name">{fieldLabel}</h4>
                  </div>
                  <div className="field-content">
                    {isFileField(key, fieldValue) ? (
                      <div className="file-list">
                        {fieldValue.map((fileUrl: any, idx: number) => {
                          const fileName = fileUrl.split("/")[fileUrl.split("/").length - 1];
                          const bucketUrl = StoreData?.userData?.userDetails?.bucketUrl || "";
                          return (
                            <a
                              href={`${bucketUrl}${fileUrl}`}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="file-item"
                              key={idx}
                            >
                              <span className="file-name">{fileName}</span>
                              <span className="download-text">Download</span>
                            </a>
                          );
                        })}
                      </div>
                    ) : Array.isArray(fieldValue) ? (
                      <div className="array-list">
                        {fieldValue.map((item: any, idx: number) => (
                          <span key={idx} className="array-tag">
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="field-value">
                        {fieldValue || (
                          <span className="no-value">No value provided</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Root>
  );
};

export default FormResponse;

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  font-family: 'Gilroy-Regular', sans-serif;

  .drawer-header {
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    background: #fafafa;

    h3 {
      margin: 0 0 16px 0;
      font-size: 20px;
      font-weight: 600;
      color: #1f2937;
      font-family: 'Gilroy-SemiBold', sans-serif;
    }

    .action-btns {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .btn {
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      border: none;
      cursor: pointer;
      transition: all 0.2s;
      font-family: 'Gilroy-SemiBold', sans-serif;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
      }

      &:active {
        transform: translateY(0);
      }
    }

    .btn-download {
      background: #3b82f6;
      color: white;

      &:hover {
        background: #2563eb;
      }
    }

    .btn-files {
      background: #10b981;
      color: white;

      &:hover {
        background: #059669;
      }
    }
  }

  .submission-info {
    padding: 12px 24px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-size: 13px;
    color: #6b7280;
    font-family: 'Gilroy-Medium', sans-serif;
  }

  .drawer-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px 24px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;

      &:hover {
        background: #a1a1a1;
      }
    }
  }

  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #9ca3af;

    p {
      margin: 0;
      font-size: 15px;
    }
  }

  .fields-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .field-card {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    transition: border-color 0.2s;

    &:hover {
      border-color: #d1d5db;
    }
  }

  .field-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  .field-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    background: #3b82f6;
    color: white;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .field-name {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #374151;
    text-transform: capitalize;
    font-family: 'Gilroy-SemiBold', sans-serif;
  }

  .field-content {
    margin-left: 36px;
  }

  .field-value {
    margin: 0;
    font-size: 14px;
    color: #1f2937;
    line-height: 1.5;
    word-break: break-word;

    .no-value {
      color: #9ca3af;
      font-style: italic;
    }
  }

  .array-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .array-tag {
    background: #fff;
    border: 1px solid #d1d5db;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 13px;
    color: #374151;
  }

  .file-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .file-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;
    border: 1px solid #d1d5db;
    padding: 10px 12px;
    border-radius: 6px;
    text-decoration: none;
    transition: all 0.2s;

    &:hover {
      border-color: #3b82f6;
      background: #eff6ff;
    }

    .file-name {
      flex: 1;
      font-size: 13px;
      color: #1f2937;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-right: 12px;
    }

    .download-text {
      font-size: 12px;
      color: #3b82f6;
      font-weight: 600;
      white-space: nowrap;
    }
  }

  /* Group Field Styles */
  .group-field-card {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border: 2px solid #bae6fd;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
  }

  .group-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid #7dd3fc;
  }

  .group-icon {
    font-size: 24px;
  }

  .group-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #0c4a6e;
    font-family: 'Gilroy-Bold', sans-serif;
  }

  .group-content {
    display: flex;
    flex-direction: column;
    gap: 14px;
    background: white;
    padding: 16px;
    border-radius: 8px;
  }

  .nested-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    background: #f8fafc;
    border-left: 3px solid #3b82f6;
    border-radius: 4px;
  }

  .nested-label {
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    text-transform: capitalize;
    margin: 0;
    font-family: 'Gilroy-SemiBold', sans-serif;
  }

  .nested-value {
    font-size: 14px;
    color: #1e293b;
    font-weight: 500;
    word-break: break-word;

    span.no-value {
      color: #94a3b8;
      font-style: italic;
    }
  }

  @media (max-width: 768px) {
    .drawer-header {
      padding: 16px;

      h3 {
        font-size: 18px;
        margin-bottom: 12px;
      }

      .action-btns {
        flex-direction: column;

        .btn {
          width: 100%;
          justify-content: center;
        }
      }
    }

    .submission-info {
      padding: 10px 16px;
      font-size: 12px;
    }

    .drawer-content {
      padding: 16px;
    }

    .field-content {
      margin-left: 0;
      margin-top: 8px;
    }

    .group-field-card {
      padding: 16px;
    }

    .group-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .group-content {
      padding: 12px;
    }
  }
`;
